export default class ApplicationHandler {

    /**
     * Constructor
     *
     * @param formWrapperSelector
     * @param formSelector
     * @param submitButtonSelector
     * @param fileInputSelector
     */
    constructor(formWrapperSelector, formSelector, submitButtonSelector, fileInputSelector) {
        this.formWrapper = document.querySelector(formWrapperSelector)
        this.form = document.querySelector(formSelector);

        if (!this.form) {
            return null;
        }

        if (this.form) {
            this.submitButton = this.form.querySelector(submitButtonSelector);
            this.fileInput = this.form.querySelector(fileInputSelector);
            this.privacyRadios = this.form ? this.form.querySelectorAll('input[name="privacy_policy"]') : [];

            this.init();
        }
    }

    /**
     * Initialize event listeners
     */
    init() {
        this.apiSettings = window.wpApiSettings || {};
        this.selectedFiles = []; // Array to store selected files for better management (especially for duplicates and removals)
        if (this.submitButton) {
            this.submitButton.addEventListener('click', (e) => this.handleSubmit(e));
        }
        if (this.fileInput) {
            this.setupDragAndDrop();
            this.fileInput.addEventListener('change', () => this.handleFileUpload());
        }
        if (this.privacyRadios.length > 0) {
            this.privacyRadios.forEach(radio => {
                radio.addEventListener('change', () => {
                    const isAnyChecked = Array.from(this.privacyRadios).some(r => r.checked);
                    this.submitButton.disabled = !isAnyChecked;
                });
            });

            const isAnyChecked = Array.from(this.privacyRadios).some(r => r.checked);
            this.submitButton.disabled = !isAnyChecked;
        }

        this.form.addEventListener('input', () => this.resetSubmitState());
        this.form.addEventListener('change', () => this.resetSubmitState());

        this.sourceSelect = this.form ? this.form.querySelector('#source') : null;
        this.personalRecommendationWrapper = this.form ? this.form.querySelector('.source_personal_recommendation_wrapper') : null;
        this.personalRecommendationField = this.form ? this.form.querySelector('#source_personal_recommendation') : null;

        if (this.sourceSelect && this.personalRecommendationWrapper) {
            this.sourceSelect.addEventListener('change', () => {
                if (this.sourceSelect.value === 'custom_option_60298') {
                    this.personalRecommendationWrapper.classList.add('show');
                } else {
                    this.personalRecommendationWrapper.classList.remove('show');
                    this.personalRecommendationField.value = ''; // Wert zurücksetzen
                }
            });
        }
    }

    /**
     * Check if a file type is allowed
     *
     * @param file
     * @returns {boolean}
     */
    isAllowedFileType(file) {
        const allowedExtensions = ['.pdf', '.docx', '.jpg', '.jpeg'];
        const allowedMimeTypes = [
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'image/jpeg',
        ];
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        return allowedExtensions.includes(ext) && allowedMimeTypes.includes(file.type);
    }

    /**
     * Handle file upload and display selected file names
     */
    handleFileUpload() {
        const duplicates = [];
        const rejected = [];

        for (let i = 0; i < this.fileInput.files.length; i++) {
            const file = this.fileInput.files[i];

            if (!this.isAllowedFileType(file)) {
                rejected.push(file.name);
                continue;
            }

            const isDuplicate = this.selectedFiles.some(
                f => f.name === file.name && f.size === file.size
            );
            if (!isDuplicate) {
                this.selectedFiles.push(file);
            } else {
                duplicates.push(file.name);
            }
        }

        if (rejected.length > 0) {
            this.showFieldNotice(
                `Nicht erlaubter Dateityp: ${rejected.join(', ')}. Erlaubt sind: PDF, DOCX, JPG, JPEG.`,
                this.fileInput
            );
        }

        if (duplicates.length > 0) {
            this.showDuplicateNotice(duplicates);
        }

        this.renderFileList();
    }

    /**
     * Render the file list with remove buttons
     */
    renderFileList() {
        const existingList = this.fileInput.parentNode.querySelector('ul');
        if (existingList) {
            existingList.remove();
        }

        const fileListContainer = document.getElementById('file-list');
        if (fileListContainer) {
            fileListContainer.innerHTML = '';
        }

        const fileList = document.createElement('ul');

        this.selectedFiles.forEach((file, index) => {
            const listItem = document.createElement('li');
            listItem.className = 'file-list-item';

            const fileInfo = document.createElement('span');
            fileInfo.textContent = `${file.name} (${(file.size / 1024).toFixed(2)} KB)`;

            const removeButton = document.createElement('button');
            removeButton.type = 'button';
            removeButton.className = 'file-remove-btn';
            removeButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M23.954 21.03l-9.184-9.095 9.092-9.174-2.832-2.807-9.09 9.179-9.176-9.088-2.81 2.81 9.186 9.105-9.095 9.184 2.81 2.81 9.112-9.192 9.18 9.1z"/></svg>';
            removeButton.setAttribute('aria-label', `${file.name} entfernen`);
            removeButton.addEventListener('click', () => this.removeFile(index));

            listItem.appendChild(fileInfo);
            listItem.appendChild(removeButton);
            fileList.appendChild(listItem);
        });

        if (fileListContainer) {
            fileListContainer.appendChild(fileList);
        } else {
            this.fileInput.parentNode.appendChild(fileList);
        }
    }

    /**
     * Remove a file from the selected files
     *
     * @param index
     */
    removeFile(index) {
        this.selectedFiles.splice(index, 1);
        this.renderFileList();
    }

    /**
     * Get all selected files (use this in handleSubmit instead of this.fileInput.files)
     *
     * @returns {Array}
     */
    getSelectedFiles() {
        return this.selectedFiles;
    }

    /**
     * Setup drag and drop functionality
     */
    setupDragAndDrop() {
        const dropArea = this.fileInput.parentElement;

        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            dropArea.addEventListener(eventName, (e) => {
                e.preventDefault();
                e.stopPropagation();
            });
        });

        ['dragenter', 'dragover'].forEach(eventName => {
            dropArea.addEventListener(eventName, () => {
                dropArea.classList.add('drag-over');
            });
        });

        ['dragleave', 'drop'].forEach(eventName => {
            dropArea.addEventListener(eventName, () => {
                dropArea.classList.remove('drag-over');
            });
        });

        dropArea.addEventListener('drop', (e) => {
            const files = e.dataTransfer.files;
            const rejected = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                if (!this.isAllowedFileType(file)) {
                    rejected.push(file.name);
                    continue;
                }
                const isDuplicate = this.selectedFiles.some(
                    f => f.name === file.name && f.size === file.size
                );
                if (!isDuplicate) {
                    this.selectedFiles.push(file);
                }
            }
            if (rejected.length > 0) {
                this.showFieldNotice(
                    `Nicht erlaubter Dateityp: ${rejected.join(', ')}. Erlaubt sind: PDF, DOCX, JPG, JPEG.`,
                    this.fileInput
                );
            }
            this.renderFileList();
        });
    }


    /**
     * Upload document to Personio API
     *
     * @param file
     * @returns {Promise<any>}
     */
    async uploadDocument(file) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(this.apiSettings.root + 'jobs-importer/v1/upload-document', {
            method: 'POST',
            headers: {
                'X-WP-Nonce': this.apiSettings.nonce,
            },
            body: formData,
        });

        return await response.json();
    }

    /**
     * Submit application to Personio API
     *
     * @param applicationData
     * @returns {Promise<any>}
     */
    async submitApplication(applicationData) {
        const response = await fetch(this.apiSettings.root + 'jobs-importer/v1/submit-application', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': this.apiSettings.nonce,
            },
            body: JSON.stringify(applicationData),
        });

        return await response.json();
    }

    handleApplicationData() {
        const formData = new FormData(this.form);

        const applicationData = {
            job_position_id: parseInt(formData.get('jobid')),
            first_name: formData.get('first_name'),
            last_name: formData.get('last_name'),
            email: formData.get('email'),
            attributes: [
                {
                    id: 'phone',
                    value: formData.get('phone'),
                },
                {
                    id: 'gender',
                    value: formData.get('gender'),
                },
                {
                    id: 'available_from',
                    value: formData.get('available_from') || '',
                },
                {
                    id: 'salary_expectations',
                    value: formData.get('salary_expectations') || '',
                },
                {
                    id: 'custom_attribute_4401657',
                    value: formData.get('source') || 'custom_option_60310', // Fallback auf "Keine Angabe", falls kein Wert ausgewählt wurde
                },
                {
                    id: 'custom_attribute_4610809',
                    value: formData.get('privacy_policy'),
                },
                {
                    id: 'custom_attribute_4611045',
                    value: formData.get('source_personal_recommendation'),
                },
            ],
        };

        // Entferne leere/undefined Felder
        Object.keys(applicationData).forEach(key => {
            if (applicationData[key] === null || applicationData[key] === undefined || applicationData[key] === '') {
                delete applicationData[key];
            }
        });

        return applicationData;
    }

    /**
     * Handle form submission
     *
     * @param event
     * @returns {Promise<void>}
     */
    async handleSubmit(event) {
        event.preventDefault();

        if (this.isSubmitting) {
            return;
        }

        if ( !this.validateForm() ) {
            return;
        }

        this.isSubmitting = true;

        try {
            const applicationData = this.handleApplicationData();

            if (this.selectedFiles && this.selectedFiles.length > 0) {
                for (let i = 0; i < this.selectedFiles.length; i++) {
                    const uploadResult = await this.uploadDocument(this.selectedFiles[i]);
                    if (!applicationData.files) {
                        applicationData.files = [];
                    }
                    applicationData.files.push({
                        uuid: uploadResult.uuid,
                        original_filename: uploadResult.original_filename,
                        category: "cv",
                    });
                }
            }

            const result = await this.submitApplication(applicationData);

            if (result.errors) {
                if (result.errors[0].type === 'com.personio.recruiting.api.http.FieldError') {
                    if (result.errors[0].errors[0].reason === 'errors.applicant-already-exists') {
                        this.showSubmitNotice('error', 'HOPPLA! Mit dieser E-Mail-Adresse wurde für diese Stelle bereits eine Bewerbung eingereicht.');
                    } else {
                        this.showSubmitNotice('error', 'Bitte überprüfe deine Eingabe. Es sind Fehler aufgetreten.');
                    }
                } else {
                    this.showSubmitNotice('error', 'Es ist ein Fehler bei der Übermittlung deiner Bewerbung aufgetreten. Bitte versuche es später erneut.');
                }
            }

            if (result.success) {
                this.showSubmitNotice('success');
                console.log('Bewerbung erfolgreich übermittelt:', result);

                if ( typeof window.dataLayer !== 'undefined' ) {
                    window.dataLayer.push({
                        event: 'rk_form_submit',
                        form_type: 'job_application',
                        job_position_id: applicationData.job_position_id,
                    });
                }
            }
        } catch (error) {
            console.error('Fehler:', error);
        } finally {
            this.isSubmitting = false;
        }
    }

    resetSubmitState() {
        this.isSubmitting = false;
        const existing = this.submitButton.parentNode.querySelector('.field-notice');
        if (existing) {
            existing.remove();
        }
    }

    showSubmitNotice(type = 'info', message = '') {
        if (type === 'success') {
            const successWrapper = document.querySelector('.application-form--success');
            successWrapper.classList.add('show');
            this.form.remove();
        } else {
            const existing = this.submitButton.parentNode.querySelector('.field-notice');
            if (existing) {
                existing.remove();
            }
            this.showFieldNotice(message, this.submitButton);
        }
    }

    /**
     * Show notice for a field error (e.g. duplicate file upload)
     *
     * @param message - The message to display
     * @param fieldSelector - The field element to which the notice should be attached
     * @param timeout - Whether the notice should disappear after a certain time
     * @param timeoutDuration - Duration in seconds after which the notice should disappear (default: 4)
     */
    showFieldNotice(message, fieldSelector, timeout = false, timeoutDuration = 4) {
        const notice = document.createElement('div');
        notice.className = 'field-notice';
        notice.textContent = message;

        fieldSelector.parentNode.appendChild(notice);

        if (timeout) {
            setTimeout(() => {
                notice.remove();
            }, timeoutDuration * 1000);
        }
    }

    /**
     * Show notice for duplicate files
     *
     * @param {Array} duplicates - Array of duplicate file names
     */
    showDuplicateNotice(duplicates) {
        let message = `Die Datei "${duplicates[0]}" wurde bereits hinzugefügt.`;

        if (duplicates.length > 1) {
            message = `Folgende Dateien wurden bereits hinzugefügt: ${duplicates.join(', ')}`;
        }

        this.showFieldNotice(message, this.fileInput);
    }

    /**
     * Validate all required form fields before submission
     *
     * @returns {boolean}
     */
    validateForm() {
        let isValid = true;

        this.form.querySelectorAll('.field-notice').forEach(notice => notice.remove());
        this.form.querySelectorAll('.field--error').forEach(field => field.classList.remove('field--error'));

        const requiredFields = this.form.querySelectorAll('input[required], select[required], textarea[required]');
        requiredFields.forEach(field => {
            if (field.type === 'radio') {
                const groupName = field.name;
                const isGroupChecked = this.form.querySelector(`input[name="${groupName}"]:checked`);
                if (!isGroupChecked) {
                    isValid = false;
                    const fieldWrapper = field.closest('.field');
                    if (fieldWrapper) {
                        fieldWrapper.classList.add('field--error');
                    }
                }
            } else if (field.type === 'file') {
                if (this.selectedFiles.length === 0) {
                    isValid = false;
                    const fieldWrapper = field.closest('.field');
                    if (fieldWrapper) {
                        fieldWrapper.classList.add('field--error');
                        this.showFieldNotice('Bitte lade mindestens eine Datei hoch.', field);
                    }
                }
            } else {
                if (!field.value.trim()) {
                    isValid = false;
                    const fieldWrapper = field.closest('.field');
                    if (fieldWrapper) {
                        fieldWrapper.classList.add('field--error');
                        this.showFieldNotice(`Dies ist ein Pflichtfeld.`, field);
                    }
                }
            }
        });

        return isValid;
    }
}
